import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, X, Check, Phone } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";

const STATUS = ["pending", "confirmed", "checked_in", "completed", "no_show", "cancelled"];
const empty = { client_name: "", client_email: "", client_phone: "", pet_name: "", date: "", time: "", notes: "", status: "confirmed" };

export default function Appointments() {
  const [bookings, setBookings] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [filter, setFilter] = useState("all");

  const load = () => api.get("/bookings").then((r) => setBookings(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const save = async (e) => {
    e.preventDefault();
    try { await api.post("/bookings", form); toast.success("Appointment created"); setOpen(false); setForm(empty); load(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };
  const changeStatus = async (id, status) => { await api.put(`/bookings/${id}`, { status }); toast.success(`Marked ${status}`); load(); };
  const del = async (id) => { await api.delete(`/bookings/${id}`); toast.success("Deleted"); load(); };

  const shown = bookings.filter((b) => filter === "all" || b.status === filter);

  return (
    <div data-testid="appointments-page">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div><h1 className="font-display text-4xl">Appointments</h1><p className="text-brand-ink/60 mt-2">Manage bookings and confirmations.</p></div>
        <div className="flex gap-3 flex-wrap">
          <a href={`${process.env.REACT_APP_BACKEND_URL}/api/admin/bookings/export.csv`} className="inline-flex items-center gap-2 px-6 py-3 rounded-full border border-brand-line font-medium hover:border-brand-red transition-colors" data-testid="export-bookings-btn">Export CSV</a>
          <button onClick={() => { setForm(empty); setOpen(true); }} className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-brand-red text-white font-medium hover:bg-brand-reddark transition-colors" data-testid="add-appointment-btn"><Plus className="w-4 h-4" /> New appointment</button>
        </div>
      </div>

      <div className="mt-6 flex gap-2 flex-wrap">
        {["all", ...STATUS].map((s) => (
          <button key={s} onClick={() => setFilter(s)} className={`px-4 py-1.5 rounded-full text-sm capitalize transition-colors ${filter === s ? "bg-brand-ink text-white" : "bg-white border border-brand-line text-brand-ink/60 hover:border-brand-red"}`} data-testid={`filter-${s}`}>{s}</button>
        ))}
      </div>

      <div className="mt-6 bg-white rounded-2xl border border-brand-line overflow-hidden">
        {shown.length === 0 ? <div className="p-8 text-brand-ink/50" data-testid="appointments-empty">No appointments.</div> : (
          <div className="divide-y divide-brand-line">
            {shown.map((b) => (
              <div key={b.id} className="p-5 flex items-center justify-between flex-wrap gap-4" data-testid={`appointment-${b.id}`}>
                <div>
                  <div className="font-medium">{b.client_name} <span className="text-brand-ink/40 text-sm">· {b.pet_name || "pet"}</span></div>
                  <div className="text-sm text-brand-ink/50">{b.date} {b.time} · {b.client_email}</div>
                  {b.client_phone && <a href={`tel:${b.client_phone}`} className="inline-flex items-center gap-1 text-sm text-brand-red hover:text-brand-reddark transition-colors mt-1" data-testid={`appointment-phone-${b.id}`}><Phone className="w-3.5 h-3.5" /> {b.client_phone}</a>}
                  {b.notes && <div className="text-xs text-brand-ink/40 mt-1">{b.notes}</div>}
                </div>
                <div className="flex items-center gap-2">
                  <select value={b.status} onChange={(e) => changeStatus(b.id, e.target.value)} className="text-sm rounded-lg border border-brand-line px-3 py-1.5 capitalize focus:ring-2 focus:ring-brand-red focus:outline-none" data-testid={`status-select-${b.id}`}>
                    {STATUS.map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                  {b.deposit_paid && <span className="flex items-center gap-1 text-xs text-green-700"><Check className="w-3 h-3" /> paid</span>}
                  <button onClick={() => del(b.id)} className="text-brand-ink/30 hover:text-brand-red transition-colors" data-testid={`delete-appointment-${b.id}`}><Trash2 className="w-4 h-4" /></button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {open && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" data-testid="appointment-modal">
          <div className="bg-white rounded-2xl w-full max-w-lg p-8">
            <div className="flex items-center justify-between mb-6"><h2 className="font-display text-2xl">New appointment</h2><button onClick={() => setOpen(false)}><X className="w-5 h-5" /></button></div>
            <form onSubmit={save} className="grid grid-cols-2 gap-4">
              <Fld label="Client name" v={form.client_name} on={set("client_name")} req span testid="appt-name" />
              <Fld label="Email" type="email" v={form.client_email} on={set("client_email")} req testid="appt-email" />
              <Fld label="Phone" v={form.client_phone} on={set("client_phone")} testid="appt-phone" />
              <Fld label="Pet name" v={form.pet_name} on={set("pet_name")} testid="appt-pet" />
              <Fld label="Date" type="date" v={form.date} on={set("date")} req testid="appt-date" />
              <Fld label="Time" type="time" v={form.time} on={set("time")} req testid="appt-time" />
              <div className="col-span-2"><label className="text-xs uppercase tracking-[0.2em] text-brand-ink/50 font-semibold">Notes</label><textarea value={form.notes} onChange={set("notes")} rows={2} className="mt-2 w-full rounded-xl border border-brand-line px-4 py-2.5 focus:ring-2 focus:ring-brand-red focus:outline-none" data-testid="appt-notes" /></div>
              <button type="submit" className="col-span-2 px-6 py-3 rounded-full bg-brand-red text-white font-medium hover:bg-brand-reddark transition-colors" data-testid="appt-save-btn">Create appointment</button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

function Fld({ label, type = "text", v, on, req, span, testid }) {
  return (
    <div className={span ? "col-span-2" : ""}>
      <label className="text-xs uppercase tracking-[0.2em] text-brand-ink/50 font-semibold">{label}</label>
      <input type={type} value={v} onChange={on} required={req} className="mt-2 w-full rounded-xl border border-brand-line px-4 py-2.5 focus:ring-2 focus:ring-brand-red focus:outline-none" data-testid={testid} />
    </div>
  );
}
